import { connectorQueries } from "@/components/connectors/api.ts";
import { ConnectorConnectAction } from "@/components/connectors/ConnectorConnectAction.tsx";
import { ConnectorLogo } from "@/components/connectors/ConnectorLogo.tsx";
import { ConnectorTile } from "@/components/connectors/ConnectorTile.tsx";
import { getConnectorIconForVariant } from "@/components/connectors/providerIcons.tsx";
import { useConnectorCatalog } from "@/components/connectors/useConnectorCatalog.ts";
import { Input } from "@/components/ui/input.tsx";
import { connectorConnectMode } from "@/shared/connectors/connectMode.ts";
import type { ConnectorCatalogItem } from "@/types/connectors.ts";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  type ConnectorConnectedEntry,
  type ConnectorPickerFraming,
  connectorAccountsByCatalogId,
  connectorDisplayTitle,
  connectorPickerSections,
  connectorSectionsInPlace,
  connectorThreeSections,
  matchesConnectedEntrySearch,
  matchesConnectorSearch,
} from "@/shared/connectors/pickerSections.ts";

/** Show the search box only once the list is long enough to need one. */
const SEARCH_THRESHOLD = 6;

/**
 * The connector gallery, inline in a card: search a list of services and
 * connect several without leaving the conversation.
 *
 * Reads the catalog itself rather than taking a list from the artifact, through
 * the same hook the Settings gallery uses — which is what keeps the card honest,
 * flipping a tile to "Connected" in place instead of describing a world that
 * has moved on. It renders the gallery's own tiles (`ConnectorTile`) in a grid
 * that follows the card's width — one column narrow, up to three wide — so the
 * canvas and the Settings page speak one visual language; what is the card's
 * own is the artifact's pinning and scoping.
 */
export const ConnectorPickerBlock = ({
  scopeIds,
  pinnedIds,
  preferredIds,
  inPlace = false,
}: ConnectorPickerFraming & {
  /**
   * No Connected section: every service stays in its section and shows
   * Connected in place, for a surface only offering what to add.
   */
  inPlace?: boolean;
}) => {
  const [search, setSearch] = useState("");
  // Toasts off: a thread can hold more than one of these cards, and each would
  // otherwise announce the same connection.
  const { data: catalog, isLoading } = useConnectorCatalog({ showToasts: false });

  const { pinned, rest } = useMemo(
    () => connectorPickerSections(catalog?.items ?? [], { scopeIds, pinnedIds, preferredIds }),
    [catalog, scopeIds, pinnedIds, preferredIds],
  );

  // The same three-section split as the omnibox card and the same per-account
  // model as the Settings gallery: every connected account is its own card,
  // and the connect sections hold only tiles still offering an action. The
  // query gates rendering exactly like the catalog's: without it a connected
  // account has no tile, and the grid would read as "nothing connected" while
  // the data is merely in flight.
  const {
    data: accountsData,
    isLoading: accountsLoading,
    isError: accountsError,
  } = useQuery(connectorQueries.list());
  const sections = useMemo(() => {
    const connectors = accountsData?.connectors ?? [];
    const byCatalogId = connectorAccountsByCatalogId(connectors);
    return inPlace
      ? { ...connectorSectionsInPlace(pinned, rest, byCatalogId, connectors), connected: [] }
      : connectorThreeSections(pinned, rest, byCatalogId);
  }, [pinned, rest, accountsData, inPlace]);

  const visibleRecommended = sections.recommended.filter((item) =>
    matchesConnectorSearch(item, search),
  );
  const visibleConnected = sections.connected.filter((entry) =>
    matchesConnectedEntrySearch(entry, search),
  );
  const visibleAdditional = sections.additional.filter((item) =>
    matchesConnectorSearch(item, search),
  );
  const total =
    sections.recommended.length + sections.connected.length + sections.additional.length;
  const visible = visibleRecommended.length + visibleConnected.length + visibleAdditional.length;
  // Headings only when there is more than one section to tell apart — one
  // alone would label a grid against nothing, which reads as a rendering bug.
  const showHeadings =
    [visibleRecommended, visibleConnected, visibleAdditional].filter((s) => s.length > 0).length >=
    2;

  if (isLoading || accountsLoading) {
    return <p className="text-xs text-muted-foreground">Loading services…</p>;
  }
  if (accountsError) {
    return (
      <p className="text-xs text-muted-foreground">
        Couldn't load services just now. Try again in a moment.
      </p>
    );
  }
  if (total === 0) {
    return <p className="text-xs text-muted-foreground">No services are available to connect.</p>;
  }

  return (
    <div className="@container flex flex-col gap-3">
      {total >= SEARCH_THRESHOLD && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search connectors"
            className="bg-white pl-9"
          />
        </div>
      )}

      {visible === 0 ? (
        <p className="py-4 text-xs text-muted-foreground">No services match your search.</p>
      ) : (
        <>
          {showHeadings && visibleRecommended.length > 0 && (
            <SectionHeading>Recommended</SectionHeading>
          )}
          {visibleRecommended.length > 0 && (
            <TileGrid items={visibleRecommended} inPlace={inPlace} />
          )}
          {showHeadings && visibleConnected.length > 0 && (
            <SectionHeading>Connected</SectionHeading>
          )}
          {visibleConnected.length > 0 && <ConnectedTileGrid entries={visibleConnected} />}
          {showHeadings && visibleAdditional.length > 0 && (
            <SectionHeading>Additional Connectors</SectionHeading>
          )}
          {visibleAdditional.length > 0 && <TileGrid items={visibleAdditional} inPlace={inPlace} />}
        </>
      )}
    </div>
  );
};

/** Width-following card grid: one column only when truly narrow, two across
 *  at the canvas card's width, three fullscreen. Container-query driven, so
 *  it tracks the card's own width rather than the window's. */
const TileGrid = ({ items, inPlace }: { items: ConnectorCatalogItem[]; inPlace: boolean }) => (
  <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2 @4xl:grid-cols-3">
    {items.map((item) => {
      // With a Connected section, that section owns connected state and a tile
      // here only offers its action. In place, a connected tile says so and
      // offers nothing more.
      const connected = inPlace && item.connected;
      return (
        <ConnectorTile
          key={item.id}
          item={{
            name: item.name,
            description: item.description,
            websiteUrl: item.websiteUrl,
            icon: (item.provider
              ? getConnectorIconForVariant(item.provider, item.iconVariant ?? "brand")
              : null) ?? <ConnectorLogo websiteUrl={item.websiteUrl} name={item.name} />,
            connected,
            needsReconnect: connectorConnectMode(item).needsReconnect,
            action: connected ? null : (
              <ConnectorConnectAction item={item} statsigName="thread-connector-picker-connect" />
            ),
          }}
        />
      );
    })}
  </div>
);

/**
 * The Connected section's cards: one per account (the identity line carries
 * the email), or the connected service itself when the provider reports no
 * identities. Status-only — sync and remove stay on the Settings gallery,
 * whose cards these mirror.
 */
const ConnectedTileGrid = ({ entries }: { entries: ConnectorConnectedEntry[] }) => (
  <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2 @4xl:grid-cols-3">
    {entries.map(({ item, connector }) => (
      <ConnectorTile
        key={connector ? `account:${connector.id}` : item.id}
        item={{
          name: connectorDisplayTitle(item),
          // The gallery's account-card cues: the identity line is the only
          // text under the name — no catalog description and no service-host
          // line (websiteUrl deliberately not passed) on a connected card.
          description: connector ? (connector.email ?? connector.displayName) : undefined,
          icon: (item.provider
            ? getConnectorIconForVariant(item.provider, item.iconVariant ?? "brand")
            : null) ?? <ConnectorLogo websiteUrl={item.websiteUrl} name={item.name} />,
          connected: true,
          needsReconnect: connector
            ? connector.needsAuth
            : connectorConnectMode(item).needsReconnect,
          action: null,
        }}
      />
    ))}
  </div>
);

const SectionHeading = ({ children }: { children: ReactNode }) => (
  <div className="text-xs font-medium text-muted-foreground">{children}</div>
);
